import { cloneDeep } from 'lodash';
import Address from '~domain/model/application/home/address';
import { Coordinates } from '~domain/model/core/address/coordinates';
import { Province, ProvinceAbbreviation } from '~domain/model/core/address/province';
import { AddressValidator } from '~domain/validators/core/basic-types/address/address-validator';
import { removeDiacritics } from './formatters';

/**
 * Compare two addresses deeply (all fields)
 * @param address first address
 * @param reference second address
 * These fields are equals:
 * { unitNumber: undefined } and { }
 * { streetName: Test } and { streetName: test }
 * { postalCode: H1H1H1 } and { postalCode: H1H 1H1}
 */
export const isSameAddress = (address: Address, reference: Address): boolean => {
  // If both address are undefined, they are not different
  if (!address && !reference) return true;
  // If only one is undefined, than they are not the same
  if ((!address && reference) || (address && !reference)) return false;

  const address1: Address = formatAddressToCompare(cloneDeep(address));
  const address2: Address = formatAddressToCompare(cloneDeep(reference));

  // Check if base address fields are differents
  if (address1.streetName !== address2.streetName) return false;
  if (address1.streetNumber !== address2.streetNumber) return false;
  if (address1.city !== address2.city) return false;
  if (address1.province !== address2.province) return false;
  if (address1.country !== address2.country) return false;
  if (address1.postalCode !== address2.postalCode) return false;

  // If both coordinates are not defined, than the addresses are the same
  if (!address1.coordinates && !address2.coordinates) return true;
  // If only one coordinates pair is defined, than addresses are different
  if ((!address1.coordinates && address2.coordinates) || (address1.coordinates && !address2.coordinates)) return false;

  // Check if coordinates are different
  if (!areCoordinatesEqual(address1.coordinates, address2.coordinates)) return false;

  // Address are the same
  return true;
};

/**
 * Compare partially addresses
 * @param address first address
 * @param reference second address
 */
export const isPartiallySameAddress = (address: Address, reference: Address): boolean => {
  // If both address are undefined, they are not different
  if (!address && !reference) return true;
  // If only one is undefined, than they are not the same
  if ((!address && reference) || (address && !reference)) return false;

  const address1: Address = formatAddressToCompare(cloneDeep(address));
  const address2: Address = formatAddressToCompare(cloneDeep(reference));

  // Check if street name, street number and city are the same
  if (address1.streetName !== address2.streetName) return false;
  if (address1.streetNumber !== address2.streetNumber) return false;
  if (address1.city !== address2.city) return false;

  return true;
};

/**
 * Compare a pair coordinates to determine if the pair is equal.
 * Latitutde and longitude values are round to 6 decimals.
 * @param coordinates1 first coordinates
 * @param coordinates2 second coordinates
 */
export const areCoordinatesEqual = (coordinates1: Coordinates, coordinates2: Coordinates): boolean => {
  return (
    roundCoordinate(coordinates1.latitude) === roundCoordinate(coordinates2.latitude) &&
    roundCoordinate(coordinates1.longitude) === roundCoordinate(coordinates2.longitude)
  );
};

/**
 * Round coordinates to a precision of 10 cm.
 * https://en.wikipedia.org/wiki/Decimal_degrees
 * @param coordinate number to round
 */
const roundCoordinate = (coordinate: number): number => Math.round(coordinate * 1000000) / 1000000;

// Clean an address so all accents, upper cases are removed
// Postal code spaces are removed as well
const formatAddressToCompare = (address: Address): Address => {
  return {
    ...address,
    city: removeDiacritics(address.city.toLocaleLowerCase()),
    country: removeDiacritics(address.country.toLocaleLowerCase()),
    postalCode: removeDiacritics(address.postalCode.replace(/\s+/g, '').toLocaleLowerCase()),
    province: address.province,
    streetName: removeDiacritics(address.streetName.toLocaleLowerCase()),
    streetNumber: removeDiacritics(address.streetNumber.toLocaleLowerCase()),
    unit: address.unit ? removeDiacritics(address.unit.toLocaleLowerCase()) : undefined,
  };
};

export const SupportedProvinces = [Province.Quebec, Province.Ontario];

// Returns true if provided province is supported
export const isSupportedProvince = (province: Province): boolean => {
  const cleanProvince: string = removeDiacritics(province).toLowerCase();
  return SupportedProvinces.includes(cleanProvince as Province);
};

export function isAddressCompleted(address: Address): boolean {
  const errors = new AddressValidator().validate(address);

  return errors === undefined;
}

// Returns abbreviation of the specified province
// FIXME: Province in db should use abbreviation. Right now it does not so we need this helper
// Please remove when province abbreviation is used in DB.
export const getProvinceAbbreviation = (province: Province): string => {
  let result: string;
  ProvinceAbbreviation.forEach((name: Province, abbreviation: string) => {
    if (name === province) {
      result = abbreviation.toLowerCase();
      return;
    }
  });

  return result;
};
